// Generic whoop-style OAuth popup: open the provider's authorize URL in
// a popup window whose redirect lands on one of our server callback
// routes; the server completes the flow and serves
// `redirect-finish.anon.html`, which posts `{source}-auth-success` /
// `{source}-auth-failure` (+ a machine-readable `reason`) back to the
// opener and closes itself.
//
// This is the app-layer transport shared by the whoop connect flow, the
// outlook signin page, and the outlook calendar-connect descriptor. It
// deliberately knows nothing about providers: callers build the
// authorize URL (see `MicrosoftAuth`) and choose the callbacks.

import { InstanceUrl } from "@tento-core/instance-url";
import { Tracing } from "@tento-core/tracing";
import { Config } from "../core/config";

export namespace OAuthPopup {
  export interface Params {
    /** Fully-built provider authorize URL the popup navigates to. */
    authUrl: string;
    /**
     * Message-type prefix — the popup-close page posts
     * `{source}-auth-success` / `{source}-auth-failure`.
     */
    source: string;
    onSuccess: () => void;
    /** Maps the server's failure `reason` to UI; null = ignore failures. */
    onFailure: Option<(reason: string) => void>;
    /** Popup closed without a completion message (user-cancel or a
     * dropped message); null = ignore. Lets promise-shaped callers
     * settle instead of hanging. */
    onDismiss: Option<() => void>;
  }

  /**
   * Open the popup and listen for the completion message. Returns false
   * when the browser blocked the popup (callers surface their own copy).
   * A watchdog tears the listener down if the user closes the popup
   * without finishing; closing is never treated as success.
   */
  export function open(params: Params): boolean {
    const popup = window.open(
      params.authUrl,
      `${params.source}OAuth`,
      "width=500,height=700",
    );
    if (!popup) {
      return false;
    }

    let completed = false;
    const messageHandler = (event: MessageEvent) => {
      const type = event.data?.type;
      const isSuccess = type === `${params.source}-auth-success`;
      const isFailure = type === `${params.source}-auth-failure`;
      if (!isSuccess && !isFailure) return;

      // Ours by type but rejected by origin: name the drop — this
      // otherwise presents as "popup closed, nothing happened".
      const allowedOrigins = InstanceUrl.allowedOrigins();
      if (!allowedOrigins.includes(event.origin)) {
        Tracing.warn("net")(
          `oauth-popup/${params.source}: completion message REJECTED — ` +
            `origin ${event.origin} not in [${allowedOrigins.join(", ")}]`,
        );
        return;
      }

      completed = true;
      window.removeEventListener("message", messageHandler);
      clearInterval(interval);
      if (isSuccess) {
        params.onSuccess();
      } else {
        params.onFailure?.(String(event.data?.reason ?? ""));
      }
    };

    window.addEventListener("message", messageHandler);

    const interval = setInterval(() => {
      if (!popup.closed) return;
      clearInterval(interval);
      window.removeEventListener("message", messageHandler);
      if (!completed) {
        // User-cancel or a silently-dropped message — breadcrumb either way.
        Tracing.warn("net")(
          `oauth-popup/${params.source}: popup closed without a completion message`,
        );
        params.onDismiss?.();
      }
    }, 1000);

    return true;
  }
}

export namespace MicrosoftAuth {
  const AUTHORIZE_URL =
    "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";

  /** The signin popup's server callback (anon: registers + logs in). */
  export const SIGNIN_REDIRECT_PATH = "/api/user/signin/outlook/redirect";
  /** The calendar-connect popup's server callback (links to the session user). */
  export const CONNECT_REDIRECT_PATH = "/api/user/connections/outlook/redirect";

  /** Whether the Entra app registration is configured (client id known). */
  export function isConfigured(): boolean {
    return Config.OUTLOOK_CLIENT_ID !== "";
  }

  /**
   * Build the Microsoft identity-platform authorize URL. `redirectPath`
   * must be one of the registered redirect routes above; the server
   * derives the identical URI for the token exchange. Mirrors
   * tento-outlook's `OutlookApp::authorize_url`.
   */
  export function authorizeUrl(redirectPath: string): string {
    // Portless canonical redirect_uri — only dev.lona.so / lona.so are
    // registered with the provider, and the server rebuilds the SAME
    // string for the token exchange (`oauth_popup::oauth_redirect_uri`),
    // which must match byte-for-byte.
    const redirectUri = new URL(InstanceUrl.root(redirectPath));
    const params = new URLSearchParams({
      client_id: Config.OUTLOOK_CLIENT_ID,
      response_type: "code",
      response_mode: "query",
      redirect_uri: redirectUri.toString(),
      scope: Config.OUTLOOK_SCOPES,
      state: crypto.randomUUID(),
      prompt: "select_account",
    });
    return `${AUTHORIZE_URL}?${params.toString()}`;
  }
}
